/**
 * Yoldageliyo — Site genelinde global arama (search) bileşeni.
 *
 * Aynı `#searcharea` input'u: anasayfa hero, stores, view-restaurant, market sayfalarında kullanılıyor.
 * Bu modül her seferinde tek bir input'la çalışır; sayfa değişiminde tekrar
 * inject ediliyor (vite/blade) — instance yönetimi için window scope'u kullanmıyoruz.
 *
 * Davranış:
 *  - Min 2 karakter, 250ms debounce, AbortController ile yarış-koşulu yok
 *  - Sonuçlar iki bölüm: "Restoranlar" + "Ürünler" (kapsama göre dinamik)
 *  - Açık restoranlar üstte, kapalılar grayed-out altta
 *  - Click-outside, Escape ile kapanır
 *  - Boş input → otomatik kapanır
 *  - Loading skeleton + boş durum mesajı
 */

(function () {
    'use strict';

    const MIN_LEN = 2;
    const DEBOUNCE_MS = 250;
    const MAX_PRODUCTS = 8;
    const MAX_MERCHANTS = 6;

    function escapeHtml(s) {
        if (s === null || s === undefined) return '';
        return String(s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
    }

    function formatPrice(v) {
        if (v === null || v === undefined || v === '' || isNaN(parseFloat(v))) return '';
        return '₺' + parseFloat(v).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    function highlight(text, query) {
        if (!text) return '';
        if (!query) return escapeHtml(text);
        const escaped = escapeHtml(text);
        const safeQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        return escaped.replace(new RegExp(safeQuery, 'gi'), function (m) {
            return '<mark>' + m + '</mark>';
        });
    }

    function ensureContainer(input) {
        // Mevcut markup'ta input'un yakınında bir `.search-results` div'i bekleniyor.
        // 1) Aynı parent'ta bul
        let parent = input.parentElement;
        let dropdown = parent ? parent.querySelector('.search-results') : null;
        // 2) Bir üst seviyede bul (search-head, yg-hero-bar.yg-search vs.)
        if (!dropdown && parent && parent.parentElement) {
            dropdown = parent.parentElement.querySelector(':scope > .search-results');
        }
        // 3) Hiç yoksa input'un parent'ına ekle
        if (!dropdown) {
            dropdown = document.createElement('div');
            dropdown.className = 'search-results';
            (parent || document.body).appendChild(dropdown);
        }
        // Modernize edilmiş sınıfı sabit ekle ki eski koyu CSS override'lansın
        dropdown.classList.add('yg-search-dd');
        // Ensure positioning context on parent
        if (parent) {
            const cs = window.getComputedStyle(parent);
            if (cs.position === 'static') parent.style.position = 'relative';
        }
        return dropdown;
    }

    function setOpen(dd, open) {
        if (open) {
            dd.classList.add('open');
            dd.style.display = '';
        } else {
            dd.classList.remove('open');
            dd.style.display = 'none';
        }
    }

    function renderLoading(dd, query) {
        dd.innerHTML =
            '<div class="ygsd-state">' +
                '<div class="ygsd-spinner"></div>' +
                '<span>"' + escapeHtml(query) + '" aranıyor…</span>' +
            '</div>';
    }

    function renderEmpty(dd, query) {
        dd.innerHTML =
            '<div class="ygsd-state ygsd-empty">' +
                '<i class="ri-search-eye-line"></i>' +
                '<div>' +
                    '<strong>Sonuç bulunamadı</strong>' +
                    '<p>"' + escapeHtml(query) + '" için bir eşleşme yok. Farklı bir kelime deneyin.</p>' +
                '</div>' +
            '</div>';
    }

    function buildProductHref(product, ctx) {
        // ctx: { searchType, restaurantSlug }
        const id = product.id;
        if (ctx.searchType === 'market') {
            return 'yolda-geliyo-market#item-' + id;
        }
        if (ctx.searchType === 'restaurant' && ctx.restaurantSlug && product.merchant && product.merchant.slug === ctx.restaurantSlug) {
            return '#item-' + id;
        }
        if (product.merchant && product.merchant.slug) {
            return 'restaurant/' + product.merchant.slug + '#item-' + id;
        }
        return '#';
    }

    function buildMerchantHref(merchant) {
        if (!merchant) return '#';
        const isMarket = merchant.type == 2 || merchant.type === '2';
        if (isMarket) return 'yolda-geliyo-market';
        return 'restaurant/' + merchant.slug;
    }

    function isOpenMerchant(m) {
        if (!m) return false;
        if (typeof m.is_open === 'boolean') return m.is_open;
        return !!m.status;
    }

    function renderResults(dd, payload, query, ctx) {
        const products = Array.isArray(payload.products) ? payload.products : [];
        const merchants = Array.isArray(payload.merchants) ? payload.merchants : [];

        // Açık merchantlar üstte, kapalılar altta
        const sortedMerchants = merchants.slice().sort(function (a, b) {
            const oa = isOpenMerchant(a) ? 1 : 0;
            const ob = isOpenMerchant(b) ? 1 : 0;
            return ob - oa;
        }).slice(0, MAX_MERCHANTS);

        // Ürünleri de açık merchantlar önde gelecek şekilde sırala
        const sortedProducts = products.slice().sort(function (a, b) {
            const oa = isOpenMerchant(a.merchant) ? 1 : 0;
            const ob = isOpenMerchant(b.merchant) ? 1 : 0;
            return ob - oa;
        }).slice(0, MAX_PRODUCTS);

        if (sortedMerchants.length === 0 && sortedProducts.length === 0) {
            renderEmpty(dd, query);
            return;
        }

        let html = '';

        // ----- Restoranlar / Mağazalar -----
        if (sortedMerchants.length > 0) {
            html +=
                '<div class="ygsd-group">' +
                    '<div class="ygsd-group-title"><i class="ri-store-2-line"></i> Restoranlar / Mağazalar' +
                        '<span class="ygsd-count">' + sortedMerchants.length + '</span>' +
                    '</div>' +
                    '<ul class="ygsd-list">';

            sortedMerchants.forEach(function (m) {
                const isOpen = isOpenMerchant(m);
                const isBusy = !!m.is_busy;
                const href = buildMerchantHref(m);
                const thumb = m.thumbnail || m.image || '';
                const minLine = (m.minimum_purchase_amount && parseFloat(m.minimum_purchase_amount) > 0)
                    ? 'Min. ' + formatPrice(m.minimum_purchase_amount) : '';
                const deliveryLine = (m.delivery_fees && parseFloat(m.delivery_fees) > 0)
                    ? formatPrice(m.delivery_fees) + ' teslimat'
                    : 'Ücretsiz teslimat';
                const ratingLine = (m.rating && parseFloat(m.rating) > 0)
                    ? '<span class="ygsd-rating"><i class="ri-star-fill"></i> ' + parseFloat(m.rating).toFixed(1) + '</span>'
                    : '';

                let statusBadge;
                if (!isOpen) {
                    statusBadge = '<span class="ygsd-pill closed">Kapalı</span>';
                } else if (isBusy) {
                    statusBadge = '<span class="ygsd-pill busy">Yoğun</span>';
                } else {
                    statusBadge = '<span class="ygsd-pill open">Açık</span>';
                }

                html +=
                    '<li class="ygsd-item' + (isOpen ? '' : ' is-closed') + '">' +
                        '<a href="' + href + '" class="ygsd-link">' +
                            '<div class="ygsd-thumb">' +
                                (thumb ? '<img src="' + escapeHtml(thumb) + '" alt="" loading="lazy">' : '<i class="ri-store-2-line"></i>') +
                            '</div>' +
                            '<div class="ygsd-body">' +
                                '<div class="ygsd-row1">' +
                                    '<span class="ygsd-name">' + highlight(m.name, query) + '</span>' +
                                    statusBadge +
                                '</div>' +
                                '<div class="ygsd-row2">' +
                                    ratingLine +
                                    (minLine ? '<span>' + escapeHtml(minLine) + '</span>' : '') +
                                    '<span>' + escapeHtml(deliveryLine) + '</span>' +
                                '</div>' +
                            '</div>' +
                            '<i class="ri-arrow-right-s-line ygsd-chevron"></i>' +
                        '</a>' +
                    '</li>';
            });

            html += '</ul></div>';
        }

        // ----- Ürünler -----
        if (sortedProducts.length > 0) {
            html +=
                '<div class="ygsd-group">' +
                    '<div class="ygsd-group-title"><i class="ri-restaurant-line"></i> Ürünler' +
                        '<span class="ygsd-count">' + sortedProducts.length + '</span>' +
                    '</div>' +
                    '<ul class="ygsd-list">';

            sortedProducts.forEach(function (p) {
                const merchant = p.merchant || {};
                const isOpen = isOpenMerchant(merchant);
                const href = buildProductHref(p, ctx);
                const thumb = p.thumbnail || '';
                let statusBadge;
                if (!isOpen) {
                    statusBadge = '<span class="ygsd-pill closed">Kapalı</span>';
                } else {
                    statusBadge = '<span class="ygsd-pill open">Açık</span>';
                }

                html +=
                    '<li class="ygsd-item' + (isOpen ? '' : ' is-closed') + '">' +
                        '<a href="' + href + '" class="ygsd-link" data-type="search-result">' +
                            '<div class="ygsd-thumb">' +
                                (thumb ? '<img src="' + escapeHtml(thumb) + '" alt="" loading="lazy">' : '<i class="ri-restaurant-line"></i>') +
                            '</div>' +
                            '<div class="ygsd-body">' +
                                '<div class="ygsd-row1">' +
                                    '<span class="ygsd-name">' + highlight(p.name, query) + '</span>' +
                                    '<span class="ygsd-price">' + formatPrice(p.price) + '</span>' +
                                '</div>' +
                                '<div class="ygsd-row2">' +
                                    '<span class="ygsd-merchant"><i class="ri-store-2-line"></i> ' + escapeHtml(merchant.name || '—') + '</span>' +
                                    statusBadge +
                                '</div>' +
                            '</div>' +
                        '</a>' +
                    '</li>';
            });

            html += '</ul></div>';
        }

        html +=
            '<div class="ygsd-footer">' +
                '<i class="ri-information-line"></i> Açık restoranlardan sipariş verebilirsiniz. Kapalı olanlar bilgilendirme amaçlıdır.' +
            '</div>';

        dd.innerHTML = html;
    }

    function init(input) {
        if (!input || input.dataset.ygSearchInited === '1') return;
        input.dataset.ygSearchInited = '1';
        input.setAttribute('autocomplete', 'off');

        const dd = ensureContainer(input);
        setOpen(dd, false);

        const ctx = {
            searchType: input.getAttribute('data-search-type') || null,
            restaurantSlug: input.getAttribute('data-merchant-slug') || null,
        };

        let debounceId = null;
        let aborter = null;
        let lastQuery = '';

        function close() { setOpen(dd, false); }
        function open() { setOpen(dd, true); }

        function performSearch(q) {
            if (aborter) aborter.abort();
            aborter = new AbortController();
            renderLoading(dd, q);
            open();

            const body = {
                keyword: q,
                searchMarket: ctx.searchType === 'market',
                restaurantSlug: ctx.restaurantSlug || null,
            };

            // Use existing global `api` axios instance if present, else fetch.
            const promise = (window.api && typeof window.api.post === 'function')
                ? window.api.post('/search', body, { signal: aborter.signal }).then(r => r.data)
                : fetch('/api/v1/search', {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify(body),
                    signal: aborter.signal,
                  }).then(r => r.json());

            promise.then(function (resp) {
                if (lastQuery !== q) return; // stale
                const payload = resp && resp.data ? resp.data : (resp || {});
                // SearchService boş için `false` döndürebiliyor; normalize edelim.
                if (payload === false || payload === null) {
                    renderEmpty(dd, q);
                    return;
                }
                if (payload.products === false) payload.products = [];
                if (payload.merchants === false) payload.merchants = [];
                renderResults(dd, payload, q, ctx);
            }).catch(function (err) {
                if (err && (err.name === 'AbortError' || err.name === 'CanceledError' || err.code === 'ERR_CANCELED')) return;
                console.error('[search] failed', err);
                dd.innerHTML =
                    '<div class="ygsd-state ygsd-empty">' +
                        '<i class="ri-error-warning-line"></i>' +
                        '<div><strong>Bir hata oluştu</strong><p>Lütfen daha sonra tekrar deneyin.</p></div>' +
                    '</div>';
            });
        }

        function onInput() {
            const q = (input.value || '').trim();
            lastQuery = q;
            if (debounceId) clearTimeout(debounceId);
            if (q.length < MIN_LEN) {
                if (aborter) aborter.abort();
                close();
                dd.innerHTML = '';
                return;
            }
            debounceId = setTimeout(function () { performSearch(q); }, DEBOUNCE_MS);
        }

        input.addEventListener('input', onInput);
        input.addEventListener('focus', function () {
            if ((input.value || '').trim().length >= MIN_LEN) open();
        });

        // Click outside closes
        document.addEventListener('mousedown', function (e) {
            if (!dd.classList.contains('open')) return;
            if (e.target === input || dd.contains(e.target)) return;
            close();
        });
        // Escape closes
        document.addEventListener('keydown', function (e) {
            if (e.key === 'Escape') close();
        });
        // Submit (Enter) → ilk eşleşmeye git
        input.addEventListener('keydown', function (e) {
            if (e.key === 'Enter') {
                const first = dd.querySelector('.ygsd-link');
                if (first) {
                    e.preventDefault();
                    window.location.href = first.getAttribute('href');
                }
            }
        });
        // Bir sonuç tıklandığında modal/dropdown kapanması istenebilir
        dd.addEventListener('click', function (e) {
            const link = e.target.closest && e.target.closest('a.ygsd-link');
            if (link) close();
        });
    }

    function bootstrap() {
        const input = document.getElementById('searcharea');
        if (input) init(input);
    }

    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', bootstrap);
    } else {
        bootstrap();
    }
})();
